import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Form,
  Input,
  Select,
  message,
  Spin,
  Empty,
  Breadcrumb,
  Divider,
  Tag
} from 'antd';
import {
  PlusOutlined,
  SyncOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import './SupportTicketsPage.scss';
import SupportIcon from '../../components/SupportIcon/SupportIcon';

const { TextArea } = Input;
const { Option } = Select;

type TicketStatus = 'Open' | 'In Progress' | 'On Hold' | 'Closed' | 'Resolved';
type TicketPriority = 'Low' | 'Medium' | 'High';

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  createdTime: string;
  modifiedTime: string;
  description?: string;
  thread?: any[];
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; bg: string }> = {
  Open: { color: '#ff7c02', bg: '#fff5ec', icon: <ExclamationCircleOutlined /> },
  'In Progress': { color: '#1677ff', bg: '#e6f4ff', icon: <ClockCircleOutlined /> },
  'On Hold': { color: '#faad14', bg: '#fffbe6', icon: <ClockCircleOutlined /> },
  Resolved: { color: '#52c41a', bg: '#f6ffed', icon: <CheckCircleOutlined /> },
  Closed: { color: '#8c8c8c', bg: '#f5f5f5', icon: <CloseCircleOutlined /> }
};

const PRIORITY_CONFIG: Record<string, { color: string; bg: string }> = {
  High: { color: '#e53935', bg: '#ffebee' },
  Medium: { color: '#ff7c02', bg: '#fff5ec' },
  Low: { color: '#43a047', bg: '#e8f5e9' }
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['Open'];
  return (
    <span className="st-status-badge" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.icon}&nbsp;{status}
    </span>
  );
};

const PriorityDot = ({ priority }: { priority: string }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG['Medium'];
  return (
    <span className="st-priority-dot" style={{ color: cfg.color, background: cfg.bg }}>
      {priority}
    </span>
  );
};

export default function SupportTicketsPage() {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}` };

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState<'list' | 'new' | 'detail'>('list');
  const [form] = Form.useForm();

  const fetchTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/tickets`, { headers });
      setTickets(res.data?.tickets || []);
    } catch (err: any) {
      if (err?.response?.status !== 500) {
        message.error('Failed to load tickets. Please try again.');
      }
      console.error('Ticket load error:', err?.response?.data || err.message);
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleSelectTicket = async (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setView('detail');
    setLoadingDetail(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/tickets/${ticket.id}`,
        { headers }
      );
      setSelectedTicket(res.data);
    } catch {
      // keep the list-level data
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/createticket`,
        { userId, subject: values.subject, category: values.category, priority: values.priority, description: values.description },
        { headers }
      );
      message.success("Ticket submitted! You'll receive a confirmation email shortly.");
      form.resetFields();
      setView('list');
      await fetchTickets();
    } catch (err: any) {
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error?.message;
      message.error(serverMsg || 'Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── View: New Ticket Form ────────────────────────────────────────────────
  if (view === 'new') {
    return (
      <div className="st-page">
        <div className="st-topbar">
          <div className="st-topbar-left">
            <span className="st-back-link" onClick={() => { form.resetFields(); setView('list'); }}>
              <ArrowLeftOutlined /> Back to Tickets
            </span>
            <h1 className="st-page-title">Create New Ticket</h1>
          </div>
        </div>

        <div className="st-form-card">
          <div className="st-form-tip">
            <SupportIcon style={{ filter: 'brightness(0) saturate(100%) invert(44%) sepia(94%) saturate(1012%) hue-rotate(1deg) brightness(103%)', marginRight: 8 }} />
            Describe your issue and our support team will get back to you shortly.
          </div>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ category: 'General Inquiry', priority: 'Medium' }}
            className="st-form"
          >
            <Form.Item label="Subject" name="subject" rules={[{ required: true, message: 'Please enter a subject' }, { whitespace: true }]}>
              <Input placeholder="Briefly describe your issue" size="large" className="st-input" />
            </Form.Item>

            <div className="st-form-row">
              <Form.Item label="Category" name="category" rules={[{ required: true }]} className="st-form-half">
                <Select size="large">
                  <Option value="General Inquiry">General Inquiry</Option>
                  <Option value="Technical Support">Technical Support</Option>
                  <Option value="Billing">Billing</Option>
                  <Option value="Others">Others</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Priority" name="priority" rules={[{ required: true }]} className="st-form-half">
                <Select size="large">
                  <Option value="Low"><div className="st-priority-dot-icon low" /> Low</Option>
                  <Option value="Medium"><div className="st-priority-dot-icon medium" /> Medium</Option>
                  <Option value="High"><div className="st-priority-dot-icon high" /> High</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item label="Description" name="description" rules={[{ required: true, message: 'Please describe your issue' }, { whitespace: true }]}>
              <TextArea
                placeholder="Describe your issue in detail — include steps to reproduce, error messages, etc."
                rows={7}
                className="st-textarea"
              />
            </Form.Item>

            <div className="st-form-actions">
              <Button type="default" className="st-btn-ghost" onClick={() => { form.resetFields(); setView('list'); }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting} icon={<CheckOutlined />} className="st-btn-primary">
                Submit Ticket
              </Button>
            </div>
          </Form>
        </div>
      </div>
    );
  }

  // ── View: Ticket Detail ──────────────────────────────────────────────────
  if (view === 'detail' && selectedTicket) {
    return (
      <div className="st-page">
        <div className="st-topbar">
          <div className="st-topbar-left">
            <span className="st-back-link" onClick={() => setView('list')}>
              <ArrowLeftOutlined /> Back to Tickets
            </span>
            <div className="st-title-row">
              <h1 className="st-page-title">{selectedTicket.subject}</h1>
              <span className="st-ticket-ref">#{selectedTicket.ticketNumber}</span>
            </div>
          </div>
        </div>

        {loadingDetail ? (
          <div className="st-center-spin"><Spin size="large" /></div>
        ) : (
          <div className="st-detail-card">
            <div className="st-detail-meta">
              <div className="st-meta-chip">
                <span className="st-meta-label">Status</span>
                <StatusBadge status={selectedTicket.status} />
              </div>
              <div className="st-meta-chip">
                <span className="st-meta-label">Priority</span>
                <PriorityDot priority={selectedTicket.priority} />
              </div>
              <div className="st-meta-chip">
                <span className="st-meta-label">Category</span>
                <span className="st-meta-val">{selectedTicket.category || '—'}</span>
              </div>
              <div className="st-meta-chip">
                <span className="st-meta-label">Created</span>
                <span className="st-meta-val">
                  {selectedTicket.createdTime
                    ? new Date(selectedTicket.createdTime).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '—'}
                </span>
              </div>
            </div>

            <Divider />

            <div className="st-detail-section">
              <h4 className="st-section-label">Description</h4>
              <div className="st-description-box">
                {selectedTicket.description || <em style={{ color: '#9ca3af' }}>No description provided.</em>}
              </div>
            </div>

            {selectedTicket.thread && selectedTicket.thread.length > 0 && (
              <>
                <Divider />
                <div className="st-detail-section">
                  <h4 className="st-section-label">Conversation Thread</h4>
                  <div className="st-thread">
                    {selectedTicket.thread.map((item: any, idx: number) => (
                      <div key={idx} className="st-thread-item">
                        <div className="st-thread-header">
                          <span className="st-thread-author">{item.author?.name || item.fromMailId || 'Support'}</span>
                          <span className="st-thread-time">
                            {item.createdTime
                              ? new Date(item.createdTime).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                              : ''}
                          </span>
                        </div>
                        <div className="st-thread-body" dangerouslySetInnerHTML={{ __html: item.content || item.plainText || '' }} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── View: Ticket List (default) ──────────────────────────────────────────
  return (
    <div className="st-page">
      <div className="st-topbar">
        <div className="st-topbar-left">
          <h1 className="st-page-title">
            <SupportIcon className="st-title-icon" /> Support Tickets
          </h1>
          <p className="st-page-sub">View and manage your support requests</p>
        </div>
        <div className="st-topbar-right">
          <Button type="default" className="st-btn-refresh" onClick={fetchTickets} disabled={loadingTickets} title="Refresh">
            <SyncOutlined spin={loadingTickets} />
          </Button>
          <Button type="primary" className="st-btn-primary-outline" onClick={() => setView('new')}>
            <PlusOutlined /> New Ticket
          </Button>
        </div>
      </div>

      {loadingTickets && tickets.length === 0 ? (
        <div className="st-center-spin"><Spin size="large" /></div>
      ) : tickets.length === 0 ? (
        <div className="st-empty-state">
          <SupportIcon className="st-empty-icon" />
          <h3>No tickets yet</h3>
          <p>Raise a support request and track its progress here. Only your tickets are shown.</p>
          <button className="st-btn-primary" onClick={() => setView('new')}>
            <PlusOutlined /> Create Your First Ticket
          </button>
        </div>
      ) : (
        <div className="st-ticket-table">
          <div className="st-table-head">
            <span className="st-col-num">#</span>
            <span className="st-col-subject">Subject</span>
            <span className="st-col-cat">Category</span>
            <span className="st-col-priority">Priority</span>
            <span className="st-col-status">Status</span>
            <span className="st-col-date">Created</span>
          </div>
          {tickets.map((ticket) => (
            <div key={ticket.id} className="st-table-row" onClick={() => handleSelectTicket(ticket)}>
              <span className="st-col-num st-ticket-num">#{ticket.ticketNumber}</span>
              <span className="st-col-subject st-ticket-subject">{ticket.subject}</span>
              <span className="st-col-cat st-ticket-cat">{ticket.category || '—'}</span>
              <span className="st-col-priority"><PriorityDot priority={ticket.priority} /></span>
              <span className="st-col-status"><StatusBadge status={ticket.status} /></span>
              <span className="st-col-date st-ticket-date">
                {ticket.createdTime
                  ? new Date(ticket.createdTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
